import { v4 as uuidv4 } from 'uuid'
import type { SchemaSection, SchemaScoreField, SchemaGeneratorState, JSONSchema, JSONSchemaProperty } from '@/api/types/schema'

/**
 * Generates a JSON schema from schema sections
 * Embeds UI metadata as const values for AI response rendering
 */
export function generateSchemaFromSections(sections: SchemaSection[], dialogTitle: string = 'Analysis Results'): JSONSchema {
  const properties: Record<string, JSONSchemaProperty> = {
    title: { type: "string", const: dialogTitle }
  }
  const required: string[] = ["title"]
  
  sections.forEach(section => {
    const sectionProperties: Record<string, JSONSchemaProperty> = {
      // Embed metadata as const values (for AI to return)
      title: { type: "string", const: section.title },
      order: { type: "integer", const: section.order }
    }
    const sectionRequired: string[] = ["title", "order"]
    
    // Only include icon if one is selected
    if (section.icon) {
      sectionProperties.icon = { type: "string", const: section.icon }
      sectionRequired.push("icon")
    }
    
    // Add details_title only if rationale is enabled
    if (section.includeRationale) {
      sectionProperties.details_title = { type: "string", const: section.details_title }
      sectionRequired.push("details_title")
    }
    
    // Add score fields
    section.scores.forEach(score => {
      sectionProperties[score.id] = {
        type: "object",
        additionalProperties: false,
        properties: {
          title: { type: "string", const: score.title },
          score: { 
            type: "integer", 
            minimum: score.min ?? 1, 
            maximum: score.max ?? 10 
          },
          type: { type: "string", enum: ["positive", "negative", "neutral"] }
        },
        required: ["title", "score", "type"]
      }
      sectionRequired.push(score.id)
    })
    
    // Add rationale if needed
    if (section.includeRationale) {
      sectionProperties.rationale = { type: "string" }
      sectionRequired.push('rationale')
    }
    
    properties[section.id] = {
      type: "object",
      additionalProperties: false,
      properties: sectionProperties,
      required: sectionRequired
    }
    required.push(section.id)
  })
  
  return {
    type: "object",
    additionalProperties: false,
    properties,
    required
  }
}

/**
 * Extracts schema sections from a JSON schema for editing
 */
export function extractSectionsFromSchema(schema: unknown): SchemaSection[] {
  if (!schema || typeof schema !== 'object') {
    return []
  }
  
  try {
    const parsedSchema = schema as JSONSchema
    
    if (!parsedSchema.properties) {
      return []
    }
    
    const sections: SchemaSection[] = []
    
    // Process each section (skip title field)
    for (const [sectionId, sectionSchema] of Object.entries(parsedSchema.properties)) {
      if (sectionId === 'title' || !sectionSchema.properties) continue
      
      const properties = sectionSchema.properties
      const required = sectionSchema.required || []
      
      // Extract UI metadata from const values
      const sectionTitle = String(properties.title?.const || sectionId)
      const icon = properties.icon?.const ? String(properties.icon.const) : undefined
      const order = Number(properties.order?.const || 1)
      const details_title = String(properties.details_title?.const || 'Rationale')
      const includeRationale = 'rationale' in properties && required.includes('rationale')
      
      // Extract score fields
      const scores: SchemaScoreField[] = []
      
      for (const [propKey, propSchema] of Object.entries(properties)) {
        if (propKey === 'title' || propKey === 'icon' || propKey === 'order' || 
            propKey === 'details_title' || propKey === 'rationale') continue
        
        if (propSchema.type === 'object' && propSchema.properties?.score?.type === 'integer') {
          const scoreSchema = propSchema.properties.score
          
          // Parse min/max values, handling both string and number inputs
          const parseScoreValue = (value: any): number | undefined => {
            if (typeof value === 'number' && !isNaN(value)) {
              return value
            }
            if (typeof value === 'string') {
              const parsed = parseInt(value, 10)
              return !isNaN(parsed) ? parsed : undefined
            }
            return undefined
          }
          
          scores.push({
            uuid: uuidv4(), // Generate UUID for React keys - NOT from database
            id: propKey,
            title: String(propSchema.properties.title?.const || propKey),
            type: 'integer',
            min: parseScoreValue(scoreSchema.minimum),
            max: parseScoreValue(scoreSchema.maximum)
          })
        }
      }
      
      sections.push({
        uuid: uuidv4(), // Generate new UUID for React keys
        id: sectionId,
        title: sectionTitle,
        icon,
        order,
        scores,
        includeRationale,
        details_title
      })
    }
    
    return sections.sort((a, b) => a.order - b.order)
  } catch (error) {
    console.error('Error extracting sections from schema:', error)
    return []
  }
}

/**
 * Validates a schema generator state
 */
export function validateSchemaGeneratorState(state: SchemaGeneratorState): { valid: boolean; errors: string[] } {
  const errors: string[] = []
  
  if (state.sections.length === 0) {
    errors.push("At least one analysis section is required")
  }
  
  state.sections.forEach((section, index) => {
    if (!section.id.trim()) {
      errors.push(`Section ${index + 1} must have an ID`)
    }
    
    if (!section.title.trim()) {
      errors.push(`Section "${section.id}" must have a title`)
    }
    
    if (section.scores.length === 0) {
      errors.push(`Section "${section.title}" needs at least one score field`)
    }
    
    section.scores.forEach((score, scoreIndex) => {
      if (!score.id.trim()) {
        errors.push(`Score ${scoreIndex + 1} in section "${section.title}" must have an ID`)
      }
      
      if (!score.title.trim()) {
        errors.push(`Score "${score.id}" in section "${section.title}" must have a title`)
      }
    })
  })
  
  return { valid: errors.length === 0, errors }
}

/**
 * Pretty prints a JSON schema for display with stable serialization
 */
export function formatSchemaForDisplay(schema: JSONSchema): string {
  // Use a replacer function to ensure consistent number formatting
  return JSON.stringify(schema, (_key, value) => {
    // Ensure numbers are always serialized as numbers, not strings
    if (typeof value === 'number' && !isNaN(value)) {
      return value
    }
    // Convert string numbers back to numbers for schema properties
    if (typeof value === 'string' && /^\d+$/.test(value)) {
      const num = parseInt(value, 10)
      if (!isNaN(num)) {
        return num
      }
    }
    return value
  }, 2)
}

/**
 * Generates a summary of what the schema contains for user feedback
 */
export function generateSchemaSummary(schema: JSONSchema): string {
  const allKeys = Object.keys(schema.properties || {})
  const sections = allKeys.filter(key => key !== 'title')
  
  if (sections.length === 0) {
    return "No analysis sections configured"
  }

  let summary = `Schema includes ${sections.length} analysis section${sections.length > 1 ? 's' : ''}:\n`
  
  for (const section of sections) {
    const sectionProps = schema.properties[section].properties || {}
    // Count score properties (exclude UI metadata properties and rationale)
    const scoreCount = Object.keys(sectionProps).filter(key => 
      key !== 'rationale' && key !== 'title' && key !== 'icon' && key !== 'order' && key !== 'details_title'
    ).length
    const hasRationale = 'rationale' in sectionProps
    
    summary += `• ${section}: ${scoreCount} score${scoreCount > 1 ? 's' : ''}`
    if (hasRationale) {
      summary += ' + rationale'
    }
    summary += '\n'
  }

  return summary.trim()
}

/**
 * Create a new empty section for the builder
 */
export function createNewSection(
  title: string = '',
  existingSections: SchemaSection[] = []
): SchemaSection {
  const generateSectionId = (title: string): string => {
    if (!title) return 'untitled'
    
    const baseId = title.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 50)
    
    // Make sure the ID is unique
    let finalId = baseId
    let counter = 1
    while (existingSections.some(s => s.id === finalId)) {
      finalId = `${baseId}_${counter}`
      counter++
    }
    
    return finalId
  }

  const getNextOrder = (sections: SchemaSection[]): number => {
    if (sections.length === 0) return 1
    return Math.max(...sections.map(s => s.order)) + 1
  }

  const sectionId = generateSectionId(title)
  const order = getNextOrder(existingSections)
  
  return {
    uuid: uuidv4(),
    id: sectionId,
    title,
    icon: undefined,
    order,
    scores: [],
    includeRationale: false,
    details_title: 'Rationale'
  }
}

/**
 * Create a new score for a section
 */
export function createNewScore(
  title: string = '',
  existingScores: SchemaScoreField[] = []
): SchemaScoreField {
  const generateScoreId = (title: string): string => {
    if (!title) return 'score'
    
    const baseId = title.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 30)
    
    // Make sure the ID is unique
    let finalId = baseId
    let counter = 1
    while (existingScores.some(s => s.id === finalId)) {
      finalId = `${baseId}_${counter}`
      counter++
    }
    
    return finalId
  }

  return {
    uuid: uuidv4(), // Stable identifier for React keys - NOT saved to database
    id: generateScoreId(title),
    title,
    type: 'integer',
    min: 1,
    max: 10
  }
}
